<script setup lang="ts">
import type { CardCarouselImageProps } from '.';

import { cn } from '@/utils';
import { useLazyLoadImage } from '~/composables/useLazyLoadImage';

const props = withDefaults(defineProps<CardCarouselImageProps>(), {
	sizes: 'xs:375px sm:320px md:384px lg:480px xl:583px',
	densities: 'x1'
});
const imageRef =
	useTemplateRef<ComponentPublicInstance<HTMLImageElement>>('image-ref');

const IS_BIGGER_OR_EQUAL_THREE = props.index >= 3;

const imageUrl = (
	'watermarkUrl' in props.image
		? (props.image.watermarkUrl ?? props.image.url)
		: props.image.url
)!;
const { lazilyLoadImage } = useLazyLoadImage(0.1, imageUrl);
const imageSrc = computed(() =>
	!IS_BIGGER_OR_EQUAL_THREE ? imageUrl : undefined
);

const width = computed(() => (props.variant === 'fullFace' ? 960 : 700));
const placeholder = computed(
	() =>
		(IS_BIGGER_OR_EQUAL_THREE && [50, 25, 75, 5]) as
			| [number, number, number, number]
			| false
);
// Quality 85 oferece ótimo equilíbrio entre qualidade visual e tamanho do arquivo
const quality = computed(() => (props.variant === 'fullFace' ? 85 : 80));

// Dois primeiros cards: teto do que cabe na primeira dobra em qualquer viewport.
const EAGER_CARD_POSITION_LIMIT = 1;

const isEagerImage = computed(
	() =>
		props.index === 0 &&
		(props.cardPosition === undefined ||
			props.cardPosition <= EAGER_CARD_POSITION_LIMIT)
);

onMounted(() => {
	if (!IS_BIGGER_OR_EQUAL_THREE) return;

	lazilyLoadImage(imageRef.value?.$el);
});
</script>

<template>
	<div
		class="relative flex size-full items-center justify-center"
		data-testid="carousel-image-wrapper"
		:class="
			cn(
				!props.isPropertyActive &&
					'before:content-[\'\'] before:absolute before:inset-0 before:bg-white/40 before:pointer-events-none before:z-5'
			)
		"
	>
		<NuxtImg
			ref="image-ref"
			format="webp"
			class="size-full object-cover"
			:width="width"
			:sizes="props.sizes"
			:densities="props.densities"
			:src="imageSrc"
			:alt="imageAlt"
			:loading="isEagerImage ? 'eager' : 'lazy'"
			:decoding="isEagerImage ? 'sync' : 'auto'"
			:fetchpriority="isLcpCover ? 'high' : undefined"
			:quality="quality"
			:placeholder="placeholder"
		/>
	</div>
</template>
